import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import _range from 'lodash/range';
import { FormattedMessage } from 'react-intl';

import { CheckCircleIcon } from '@/components/icons/check-circle';
import {
  HiringGoalDetails,
  OpeningDetails,
} from '@/components/pages/recommendation-posting-dialog/types';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useRecommendationContext } from '@/contexts/recommendation-context';
import { queryKeys } from '@/query-keys';

interface GeneralRecommendationProps {
  opening: OpeningDetails;
  hiringGoal: HiringGoalDetails;
}

export function GeneralRecommendation({
  opening,
  hiringGoal,
}: GeneralRecommendationProps) {
  const { openRecommendationPostingDialog } = useRecommendationContext();

  const {
    data: { recommended_channels: channels },
  } = useSuspenseQuery({
    queryKey: [
      ...queryKeys.openingDetails.channels.generalRecommendation,
      opening.slug,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsGeneralRecommendations(
        opening.slug,
      ),
  });

  if (!channels || channels.length <= 0) return null;

  const handleReviewAndApplyClick = () => {
    openRecommendationPostingDialog({
      type: 'general',
      opening,
      hiringGoal,
      recommendedChannels: channels,
    });
  };

  return (
    <div className="border-fx-neutral-200 flex flex-col gap-3 rounded-md border p-4">
      <div className="flex items-start gap-2">
        <div className="flex flex-col gap-1">
          {/* Title */}
          <Typography variant="body2">
            <FormattedMessage
              defaultMessage="Post to more channels"
              description="Short card title suggesting additional job boards for an at-risk opening"
            />
          </Typography>

          {/* Description */}
          <Typography variant="small1">
            <FormattedMessage
              defaultMessage="Post to more channels. This opening has low applicant volume for the time remaining. Similar openings at this stage receive applicants from more channels."
              description="Explains why cross-posting is suggested: low volume vs peers and remaining time"
            />
          </Typography>
        </div>

        {/* Recommended channel icons */}
        <div className="flex w-max shrink-0">
          {channels.map(channel => (
            <ChannelIcon
              key={channel.slug}
              channelSlug={channel.slug}
              className="-mr-2 last:mr-0"
            />
          ))}
        </div>
      </div>

      <Button className="h-8" onClick={handleReviewAndApplyClick}>
        <CheckCircleIcon className="size-4" />
        <FormattedMessage
          defaultMessage="Review & apply"
          description="Primary button to open multi-channel posting confirmation for this opening"
        />
      </Button>
    </div>
  );
}

GeneralRecommendation.Skeleton = function GeneralRecommendationSkeleton() {
  return (
    <div className="border-fx-neutral-200 flex flex-col gap-3 rounded-md border p-4">
      <div className="flex items-start gap-2">
        <div className="flex flex-col gap-1">
          <Skeleton className="w-50 h-5" />
          <Skeleton className="w-78 !mt-1 h-3" />
          <Skeleton className="h-3 w-80" />
          <Skeleton className="h-3 w-72" />
        </div>
        <div className="flex w-max shrink-0">
          {_range(3).map(index => (
            <Skeleton
              key={index}
              className="size-6 -mr-2 !rounded-full last:mr-0"
            />
          ))}
        </div>
      </div>

      <Skeleton className="h-8 w-full" />
    </div>
  );
};
